import {
    CompanyCustomDomainResponse,
    IAddTeamMemberUserData,
    IAdditionalCompanyInfo,
    ICompanyDomainIsValidFlags,
    ICompanyEmailTemplatesResponse,
    ICompanyOwnerNotification,
    ICompanyResponse,
    ICompanySettings,
    ICompanyThemeResponse,
    IConnectedCustomDomainResponse,
    ICreateEmailData,
    IEmailResponse,
    IInviteTeamMemberUserData,
    ILeadSource,
    IPendingTeamMemberInvite,
    IResetInstantBrandingElementsResponse,
    TEditTeamMemberRoleResponse,
    TLeadSource,
    ITag,
    IUpdateTag,
    IProjectType,
    InquiryFollowUpResponse,
    IPublishContactFormsLeadSource,
    TeamRole,
    IUpdateAssetsResponse,
    IFileDetailsPayload,
    ICompanySmsPreferences,
    TCompanyTypes,
    TCustomFieldUpdate,
    INewCompany,
    ISendClientPortalLinkData,
    IUpdateCompanyWithSuggestedAssetsResponse,
    TImageUploadType,
    IUpdateImageResponse
} from '~/types/server.types';
import { IWorkspaceCounts } from '~/components/HomePage/Pipeline/PipelineTeamMemberDropdown';
import {
    IFlowTheme,
    IFolder,
    ICustomProjectField,
    IVendorEmail,
    TCustomProjectField,
    TSmsType,
    IImage,
    ITemplateBase
} from '~/types/model.types';
import { v2CompanyGetEmailPath } from '~/utils/ts-routes.utils';
import { NetworkLabel } from '~/constants/network-lables';
import {
    Company,
    MinimalCompany,
    UpdatedClientPortalTheme
} from '~/models/company.model';
import { BaseHttpAdapter } from '~/services/adapters/base-http.adapter';
import { z } from 'zod';
import {
    TCompanySpace,
    TCompanySpaceUpdate,
    TCustomContactFieldsUpdateResponse,
    TCustomProjectFieldsUpdateResponse,
    TCustomProjectFieldUpdate,
    TInquiryFollowUpTimeFrameValue
} from '~/apps/main/CompanySettings/CompanyPreferences/server-types';
import { ICustomFieldSchema } from '~/components/CustomFields/custom-field-schemas.types';
import { UpdateCompanySocialsDto } from '~/components/CompanySettings/SpreadTheWord/types';
import { UpdateAboutCompanyDto } from '~/components/CompanySettings/AboutYourCompany/types';
import { TAvailabilityType } from '~/apps/main/Calendar/utils/calendar-page.types';
import { toBinaryBase64 } from '~/utils/text.utils';

export interface IStartInstantBrandingWebCrawlProps {
    companyId: string;
    websiteUrl: string;
}

export type CustomDomainProps = {
    companyId: string;
    domain: string;
    subdomain: string;
};

const UpdateLeadSourcesResponseSchema = z.object({
    lead_sources: z.array(TLeadSource)
});

export type TLeadSourceUpdate = Omit<ILeadSource, '_id'> &
    Partial<Pick<ILeadSource, '_id'>>;

const GetSpacesResponseSchema = z.object({
    company_spaces: z.array(TCompanySpace)
});

const UpdateCustomFieldsResponseSchema = z.object({
    custom_project_fields: z.array(TCustomProjectField)
});

const updateDefaultIsClientPortalExposed = z.object({
    exposed: z.boolean()
});

export class CompanyAdapter extends BaseHttpAdapter {
    async fetchCompany(companyId: string) {
        return this.apiService.apiRequest<ICompanyResponse>({
            method: 'get',
            url: `/api/v2/companies/${companyId}`
        });
    }

    async fetchCompanyFolders(companyId: string) {
        return this.apiService.apiRequest<IFolder[]>({
            method: 'get',
            url: `/api/v2/companies/${companyId}/folders`
        });
    }

    async workspaceCounts(selectedViewId?: string) {
        return this.apiService.apiRequest<IWorkspaceCounts>({
            method: 'get',
            url: `/api/v2/search/company_pipeline`,
            data: { viewId: selectedViewId }
        });
    }

    async updateCompanyProjectTypes({
        project_types,
        companyId,
        defaultProjectTypeLabel
    }: {
        project_types: IProjectType[];
        companyId: string;
        defaultProjectTypeLabel?: string;
    }) {
        return this.apiService.apiRequest<
            Pick<
                ICompanyResponse,
                'project_types' | 'default_project_type_label'
            >
        >({
            method: 'put',
            url: `/api/v2/companies/${companyId}/project_types`,
            data: {
                project_types,
                default_project_type_label: defaultProjectTypeLabel
            }
        });
    }

    async updateCompanyLeadSources(
        lead_sources: TLeadSourceUpdate[],
        companyId: string,
        markContactFormAsUnpublished?: boolean
    ) {
        const res = await this.apiService.apiRequest<unknown>({
            method: 'put',
            url: `/api/v2/companies/${companyId}/lead_sources`,
            data: {
                lead_sources,
                mark_contact_form_as_unpublished: markContactFormAsUnpublished
            }
        });
        return UpdateLeadSourcesResponseSchema.parse(res).lead_sources;
    }

    async getCoverImages(
        companyId: string,
        page: number,
        perPage: number
    ): Promise<{ cover_images: IImage[]; total_count: number }> {
        return this.apiService.apiRequest<{
            cover_images: IImage[];
            total_count: number;
        }>({
            method: 'get',
            url: `/api/v2/companies/${companyId}/get_cover_images`,
            params: { page, per_page: perPage }
        });
    }

    async updateSocialMediaLinks(
        companyId: string,
        payload: UpdateCompanySocialsDto
    ) {
        return this.apiService.apiRequest<unknown>({
            method: 'put',
            url: `/api/v2/companies/${companyId}`,
            data: payload
        });
    }

    async updateAboutCompany(
        companyId: string,
        payload: UpdateAboutCompanyDto
    ) {
        return this.apiService.apiRequest<unknown>({
            method: 'put',
            url: `/api/v2/companies/${companyId}`,
            data: payload
        });
    }

    async addCompanyServiceUnit(service_unit: string, companyId: string) {
        return this.apiService.apiRequest<{ service_units: string[] }>({
            method: 'post',
            url: `/api/v2/companies/${companyId}/service_units`,
            data: {
                service_unit
            }
        });
    }

    async removeCompanyServiceUnit(service_unit: string, companyId: string) {
        return this.apiService.apiRequest<{ service_units: string[] }>({
            method: 'delete',
            url: `/api/v2/companies/${companyId}/service_units`,
            data: {
                service_unit
            }
        });
    }

    protected async _getEmailTemplate(companyId: string, templateId: string) {
        return this.apiService.apiRequest<ICompanyEmailTemplatesResponse>({
            method: 'get',
            url: v2CompanyGetEmailPath(companyId, templateId)
        });
    }

    protected async _updateCompanyFlowTheme(
        companyId: string,
        theme: IFlowTheme
    ) {
        return this.apiService.apiRequest<ICompanyThemeResponse>({
            method: 'put',
            url: `/api/v2/companies/${companyId}/company_flow_theme`,
            data: {
                theme
            }
        });
    }

    async updateDisableClientPvlRequests(
        companyId: string,
        disable_client_pvl_requests: boolean
    ) {
        return this.apiService.apiRequest<void>({
            method: 'put',
            url: `/api/v2/companies/${companyId}/client_pvl_requests`,
            data: { disable_client_pvl_requests }
        });
    }

    async updateDefaultProjectAvailability(
        companyId: string,
        newAvailability: TAvailabilityType
    ) {
        return this.apiService.apiRequest<void>({
            method: 'put',
            url: `/api/v2/companies/${companyId}/default_company_availability`,
            data: { default_company_availability: newAvailability }
        });
    }

    async createFolder(companyId: string, folderName: string) {
        return this.apiService.apiRequest<IFolder>({
            method: 'post',
            url: `/api/v2/companies/${companyId}/folder`,
            data: {
                path: folderName
            }
        });
    }

    async updateFolder(folder: IFolder) {
        const { _id, company_id, path, order } = folder;

        return this.apiService.apiRequest<IFolder>({
            method: 'put',
            url: `/api/v2/companies/${company_id}/folder/${_id}`,
            data: {
                path,
                order
            }
        });
    }

    async deleteFolder(folder: IFolder) {
        const { _id, company_id } = folder;

        return this.apiService.apiRequest<IFolder>({
            method: 'delete',
            url: `/api/v2/companies/${company_id}/folder/${_id}`
        });
    }

    async deleteFont(fontName, companyId: string) {
        return this.apiService.apiRequest<any>({
            method: 'delete',
            url: `/api/v2/companies/${companyId}/remove_company_font`,
            params: {
                file_name: fontName
            }
        });
    }

    async createEmail(companyId: string, data?: ICreateEmailData) {
        return this.apiService.apiRequest<IEmailResponse>({
            method: 'post',
            url: `/api/v2/companies/${companyId}/email`,
            data
        });
    }

    async updateEmail(companyId: string, email: Partial<IVendorEmail>) {
        return this.apiService.apiRequest<IEmailResponse>({
            method: 'put',
            url: `/api/v2/companies/${companyId}/emails/${email._id}`,
            data: email,
            networkLabel: NetworkLabel.UpdateEmail
        });
    }

    async deleteEmail(companyId: string, emailId: string, force?: boolean) {
        return this.apiService.apiRequest<IEmailResponse>({
            method: 'delete',
            url: `/api/v2/companies/${companyId}/email/${emailId}`,
            ...(force && { params: { force: true } })
        });
    }

    async saveAsEmail(
        companyId: string,
        email: IVendorEmail,
        title: string
    ): Promise<IEmailResponse> {
        const encodedBody = toBinaryBase64(email.html_body);
        return this.createEmail(companyId, {
            save_as: true,
            title: title,
            html_body: encodedBody,
            subject: email.subject,
            file_attachments: email.file_attachments,
            image_attachments: email.image_attachments,
            encoded: !!encodedBody
        });
    }

    async addCompanyTeamMember({
        meta_data,
        company_id
    }: {
        meta_data: IAddTeamMemberUserData;
        company_id: string;
    }) {
        return this.apiService.apiRequest<ICompanyResponse>({
            method: 'post',
            url: `/api/v2/companies/${company_id}/team_members`,
            data: meta_data
        });
    }

    async inviteMember({
        user_data,
        company_id
    }: {
        user_data: IInviteTeamMemberUserData;
        company_id: string;
    }) {
        return this.apiService.apiRequest({
            method: 'post',
            url: `/api/v2/company/${company_id}/invite_member`,
            data: user_data
        });
    }

    async editTeamMemberRole({
        company_id,
        team_member_id,
        user_data
    }: {
        company_id: string;
        team_member_id: string;
        user_data;
    }) {
        return this.apiService.apiRequest<TEditTeamMemberRoleResponse>({
            method: 'put',
            url: `/api/v2/companies/${company_id}/team/${team_member_id}`,
            data: user_data,
            networkLabel: 'editTeamMemberRole'
        });
    }

    protected async _updateCompany({
        companyId,
        company
    }: {
        companyId: string;
        company: Partial<Company>;
    }) {
        return this.apiService.apiRequest<ICompanyResponse>({
            method: 'put',
            url: `/api/v2/companies/${companyId}`,
            data: {
                ...company
            }
        });
    }

    async fetchEmailTemplates({
        companyId,
        templateId
    }: {
        companyId: string;
        templateId: string;
    }) {
        return this.apiService.apiRequest<IEmailResponse>({
            method: 'get',
            url: `/api/v2/companies/${companyId}/email/${templateId}`
        });
    }

    async startInstantBrandingCompanyWebCrawler({
        companyId,
        websiteUrl
    }: IStartInstantBrandingWebCrawlProps) {
        return this.apiService.apiRequest<void>({
            method: 'put',
            url: `/api/v2/companies/${companyId}/start_instant_branding_web_crawl`,
            data: {
                website_url: websiteUrl
            }
        });
    }

    async generateOnboardingTemplates(companyId: string) {
        return this.apiService.apiRequest<void>({
            method: 'put',
            url: `/api/v2/companies/${companyId}/generate_onboarding_templates`
        });
    }

    async updateCompanyWithSuggestedAssets(companyId: string) {
        return this.apiService.apiRequest<IUpdateCompanyWithSuggestedAssetsResponse>(
            {
                method: 'put',
                url: `/api/v2/companies/${companyId}/link_suggested_assets_to_company`
            }
        );
    }

    async resetCompanySuggestedBrandElements({
        companyId
    }: {
        companyId: string;
    }) {
        return this.apiService.apiRequest<IResetInstantBrandingElementsResponse>(
            {
                method: 'delete',
                url: `/api/v2/companies/${companyId}/company_suggested_brand_elements`
            }
        );
    }

    async disconnectPicTimePath(companyId: string) {
        return this.apiService.apiRequest<void>({
            method: 'delete',
            url: `/api/v2/companies/${companyId}/integrations/pictime`
        });
    }

    async setPicTimeAuthCode(
        companyId: string,
        clientToken: string,
        state: string
    ) {
        return this.apiService.apiRequest<void>({
            method: 'post',
            url: `/api/v2/companies/${companyId}/integrations/pictime`,
            data: { client_token: clientToken, state: state }
        });
    }

    async getCompanyCustomDomain(companyId: string) {
        return this.apiService.apiRequest<{
            company_custom_domains: Array<CompanyCustomDomainResponse>;
        }>({
            method: 'get',
            url: `/api/v2/companies/${companyId}/company_custom_domain`
        });
    }

    async verifyDomainIsValid({
        companyId,
        hostname
    }: {
        companyId: string;
        hostname: string;
    }) {
        return this.apiService.apiRequest<ICompanyDomainIsValidFlags>({
            method: 'get',
            url: `/api/v2/companies/${companyId}/verify_domain_is_valid`,
            params: {
                hostname
            }
        });
    }

    async verifyCustomDomainAvailable({
        companyId,
        domain,
        subdomain
    }: CustomDomainProps) {
        return this.apiService.apiRequest<IConnectedCustomDomainResponse>({
            method: 'get',
            url: `/api/v2/companies/${companyId}/verify_custom_domain_available`,
            params: {
                domain,
                subdomain
            }
        });
    }

    async verifyCustomDomainDnsConfigured({
        companyId,
        domain,
        subdomain
    }: CustomDomainProps) {
        return this.apiService.apiRequest<IConnectedCustomDomainResponse>({
            method: 'get',
            url: `/api/v2/companies/${companyId}/verify_custom_domain_dns_configured`,
            params: {
                domain,
                subdomain
            }
        });
    }

    updateClientPortalConfiguration({
        companyId,
        ...data
    }: UpdatedClientPortalTheme & { companyId: string }) {
        return this.apiService.apiRequest<void>({
            method: 'put',
            url: `/api/v2/companies/${companyId}/client_portal/configuration`,
            data
        });
    }

    updateCompanyDomainPrefix({
        companyId,
        newDomainPrefix
    }: {
        companyId: string;
        newDomainPrefix: string;
    }) {
        return this.apiService.apiRequest<
            Pick<Company, '_id' | 'company_url_prefixes'>
        >({
            method: 'put',
            url: `/api/v2/companies/${companyId}/company_url_prefix/${newDomainPrefix}`
        });
    }

    async getSpaces(companyId: string): Promise<TCompanySpace[]> {
        const res = await this.apiService.apiRequest<unknown>({
            url: `/api/v2/companies/${companyId}/spaces`,
            networkLabel: 'getSpaces'
        });
        const { company_spaces } = GetSpacesResponseSchema.parse(res);
        return company_spaces;
    }

    async updateSpaces(
        companyId: string,
        company_spaces: TCompanySpaceUpdate[]
    ): Promise<void> {
        await this.apiService.apiRequest<unknown>({
            url: `/api/v2/companies/${companyId}/spaces`,
            method: 'PUT',
            data: {
                spaces: company_spaces
            }
        });
    }

    async updateCustomProjectFields(
        companyId: string,
        custom_project_fields: TCustomProjectFieldUpdate[]
    ): Promise<ICustomProjectField[]> {
        const res = await this.apiService.apiRequest<unknown>({
            url: `/api/v2/companies/${companyId}/custom_project_fields`,
            method: 'POST',
            data: { custom_project_fields }
        });

        return UpdateCustomFieldsResponseSchema.parse(res)
            .custom_project_fields;
    }

    async toggleCompanyIsClientPortalExposed(
        companyId: string,
        expose: boolean
    ) {
        const res = await this.apiService.apiRequest<boolean>({
            method: 'put',
            url: `/api/v2/companies/${companyId}/toggle_client_portal_default_exposure`,
            data: {
                expose: expose
            }
        });
        return updateDefaultIsClientPortalExposed.parse(res).exposed;
    }

    private getRequestCustomFieldSchema = (
        customFieldSchema: TCustomFieldUpdate
    ) => {
        const requestCustomFieldSchema = {
            ...customFieldSchema
        };

        // remove temporary ids that were given only to allow editing fields that weren't yet saved to server
        if (requestCustomFieldSchema._id?.indexOf('temp') > -1) {
            delete requestCustomFieldSchema._id;
        }

        // remove temporary ids from custom field options that were given only to allow editing fields that weren't yet saved to server
        if (
            ['single_select', 'multi_select'].includes(
                requestCustomFieldSchema.type
            )
        ) {
            requestCustomFieldSchema.options = (
                requestCustomFieldSchema.options ?? []
            ).map(option => ({
                ...option,
                _id: option._id?.startsWith('temp') ? undefined : option._id
            }));
        } else {
            requestCustomFieldSchema.options = undefined;
        }

        return requestCustomFieldSchema;
    };

    private getRequestCustomFieldSchemas(
        customFieldSchemas: ICustomFieldSchema[]
    ) {
        return customFieldSchemas.map((customFieldSchema, index) => {
            const requestCustomFieldSchema =
                this.getRequestCustomFieldSchema(customFieldSchema);

            requestCustomFieldSchema.order = index + 1;

            return requestCustomFieldSchema;
        });
    }

    async updateCustomProjectFieldsV2(
        companyId: string,
        customProjectFields: ICustomFieldSchema[]
    ): Promise<ICustomFieldSchema[]> {
        const res =
            await this.apiService.apiRequest<TCustomProjectFieldsUpdateResponse>(
                {
                    url: `/api/v2/companies/${companyId}/custom_project_fields`,
                    method: 'POST',
                    data: {
                        custom_project_fields:
                            this.getRequestCustomFieldSchemas(
                                customProjectFields
                            )
                    }
                }
            );

        return res?.custom_project_fields_v2;
    }

    async updateCustomContactFields(
        companyId: string,
        customContactFields: ICustomFieldSchema[]
    ): Promise<ICustomFieldSchema[]> {
        const res =
            await this.apiService.apiRequest<TCustomContactFieldsUpdateResponse>(
                {
                    url: `/api/v2/companies/${companyId}/custom_contact_fields`,
                    method: 'POST',
                    data: {
                        custom_contact_fields:
                            this.getRequestCustomFieldSchemas(
                                customContactFields
                            )
                    }
                }
            );

        return res?.custom_contact_fields;
    }

    async updateCustomField(
        companyId: string,
        customField: TCustomFieldUpdate
    ): Promise<ICustomFieldSchema> {
        const res = await this.apiService.apiRequest<ICustomFieldSchema>({
            url: `/api/v2/companies/${companyId}/custom_fields/${customField._id}`,
            method: 'PUT',
            data: {
                custom_field: this.getRequestCustomFieldSchema(customField)
            }
        });

        return res;
    }

    async updateOwnerNotification(
        companyId: string,
        payload: Pick<
            ICompanyOwnerNotification,
            'is_notification_on' | 'notification_type_cd'
        >
    ) {
        const res = await this.apiService.apiRequest<
            Pick<
                ICompanyResponse,
                'company_name' | 'company_owner_notifications'
            >
        >({
            url: `/api/v2/companies/${companyId}/notification`,
            method: 'PUT',
            data: payload
        });

        return res;
    }

    async updateCompanySettings(companyId: string, payload: ICompanySettings) {
        const res = await this.apiService.apiRequest<
            Pick<ICompanyResponse, '_id' | 'company_settings'>
        >({
            url: `/api/v2/companies/${companyId}/settings`,
            method: 'PUT',
            data: {
                company_settings: payload
            }
        });

        return res;
    }

    async getPendingTeamMemberInvites(companyId: string) {
        const res = await this.apiService.apiRequest<
            IPendingTeamMemberInvite[]
        >({
            url: `/api/v2/companies/${companyId}/get_company_pending_invite`,
            method: 'GET'
        });

        return res;
    }

    async enableTeamMember(
        companyId: string,
        payload: { enabled: boolean; team_member_id: string }
    ) {
        return this.apiService.apiRequest<ICompanyResponse>({
            url: `/api/v2/companies/${companyId}/team_members`,
            method: 'PUT',
            data: payload,
            networkLabel: `enableTeamMember__${payload.team_member_id}`
        });
    }

    async updateIsFlowThumbnailVisibleToClient(
        companyId: string,
        data: {
            is_flow_thumbnail_visible_to_client: boolean;
        }
    ) {
        await this.apiService.apiRequest<unknown>({
            url: `/api/v2/companies/${companyId}/thumbnail`,
            method: 'PUT',
            data
        });
    }

    async updateSMSEnabled(
        companyId: string,
        data: {
            value: boolean;
        }
    ) {
        return this.apiService.apiRequest<ICompanySmsPreferences>({
            url: `/api/v2/companies/${companyId}/sms_enabled`,
            method: 'PUT',
            data
        });
    }

    async updateClient2faEnabled(
        companyId: string,
        data: {
            value: boolean;
        }
    ) {
        await this.apiService.apiRequest<unknown>({
            url: `/api/v2/companies/${companyId}/client_2fa_sms_enabled`,
            method: 'PUT',
            data
        });
    }

    async updateSMSReminderEnabled(
        companyId: string,
        data: {
            value: boolean;
            reminder_type: TSmsType;
        }
    ) {
        await this.apiService.apiRequest<unknown>({
            url: `/api/v2/companies/${companyId}/sms_reminders_enabled`,
            method: 'PUT',
            data
        });
    }

    async createCompany(
        accountId: string,
        data: {
            companyType: TCompanyTypes;
            companyName: string;
        }
    ) {
        return this.apiService.apiRequest<INewCompany>({
            url: `/api/v2/account/${accountId}/company`,
            method: 'POST',
            data: {
                company_type: data.companyType,
                company_name: data.companyName
            }
        });
    }

    fetchCompanies(accountId: string) {
        return this.apiService.apiRequest<MinimalCompany[]>({
            url: `/api/v2/account/${accountId}/companies`,
            method: 'GET'
        });
    }

    getAdditionalCompanyInfo(companyId: string) {
        return this.apiService.apiRequest<IAdditionalCompanyInfo>({
            url: `/api/v2/companies/${companyId}/additional_info`
        });
    }

    updateAdditionalCompanyInfo(
        companyId: string,
        payload: IAdditionalCompanyInfo
    ) {
        this.apiService.apiRequest<IAdditionalCompanyInfo>({
            url: `/api/v2/companies/${companyId}/additional_info`,
            method: 'PUT',
            data: payload
        });
    }

    // Tags
    async getCompanyTags({ entity_id = null, tag_name = null } = {}) {
        return this.apiService.apiRequest<ITag[]>({
            url: `/api/v2/tags`,
            params: { entity_id, tag_name }
        });
    }

    async createCompanyTag(name, color) {
        return this.apiService.apiRequest<ITag>({
            url: `/api/v2/tags`,
            method: 'POST',
            data: { color, tag_name: name }
        });
    }

    async updateCompanyTag(name, color, id) {
        return this.apiService.apiRequest<IUpdateTag>({
            url: `/api/v2/tags/${id}`,
            method: 'PUT',
            data: { color, tag_name: name }
        });
    }

    async deleteCompanyTag(id: string) {
        return this.apiService.apiRequest<ITag>({
            url: `/api/v2/tags/${id}`,
            method: 'DELETE'
        });
    }

    async getTagUsageCount(id: string) {
        return this.apiService.apiRequest<number>({
            url: `/api/v2/tags/${id}`,
            method: 'GET'
        });
    }

    async addCompanyTagConnection(
        tag_id: string,
        object_type: string,
        object_id: string
    ) {
        return this.apiService.apiRequest<ITag>({
            url: `/api/v2/tags/connect_tag`,
            method: 'POST',
            data: { tag_id, object_type, object_id }
        });
    }

    async deleteCompanyTagConnection(tag_id: string, object_id: string) {
        return this.apiService.apiRequest<ITag>({
            url: `/api/v2/tags/disconnect_tag`,
            method: 'DELETE',
            data: { tag_id, object_id }
        });
    }

    async removeAsset(companyId: string, assetId: string) {
        return this.apiService.apiRequest<ICompanyResponse>({
            method: 'delete',
            url: `/api/v2/companies/${companyId}/assets/${assetId}`
        });
    }

    async updateAssetsInDB({
        companyId,
        fileDetails
    }: {
        companyId: string;
        fileDetails: IFileDetailsPayload;
    }) {
        return this.apiService.apiRequest<IUpdateAssetsResponse>({
            method: 'post',
            url: `/api/v2/companies/${companyId}/assets`,
            data: fileDetails
        });
    }

    async updateAssetsInDBWithSlimResponse({
        companyId,
        fileDetails
    }: {
        companyId: string;
        fileDetails: IFileDetailsPayload;
    }) {
        return this.apiService.apiRequest<IUpdateAssetsResponse>({
            method: 'post',
            url: `/api/v2/companies/${companyId}/assets_slim`,
            data: fileDetails
        });
    }

    getInquiryFollowUp() {
        return this.apiService.apiRequest<InquiryFollowUpResponse>({
            method: 'GET',
            url: `/api/v2/inquiry_follow_up`
        });
    }

    updateInquiryFollowUp(
        timeframeValue: TInquiryFollowUpTimeFrameValue,
        optIn?: boolean
    ) {
        return this.apiService.apiRequest({
            method: 'PUT',
            url: `/api/v2/update_inquiry_follow_up`,
            data: {
                opt_in: optIn,
                timeframe_value: timeframeValue
            }
        });
    }

    publishContactFormsLeadSource(companyId: string) {
        return this.apiService.apiRequest<IPublishContactFormsLeadSource>({
            method: 'post',
            url: `/api/v2/companies/${companyId}/contact_forms/lead_source/publish`,
            networkLabel: 'publishContactFormsLeadSource'
        });
    }

    connectPrismmAccount(prismmUserId: string, companyId: string) {
        return this.apiService.apiRequest<void>({
            method: 'post',
            url: `/api/v2/companies/${companyId}/integrations/prismm`,
            data: { prismm_user_id: prismmUserId }
        });
    }

    disconnectPrismmAccount(companyId: string) {
        return this.apiService.apiRequest<void>({
            method: 'delete',
            url: `/api/v2/companies/${companyId}/integrations/prismm`
        });
    }

    updateCompanySettingsPermissions(
        companyId: string,
        role: string
    ): Promise<{ role: TeamRole }> {
        return this.apiService.apiRequest<any>({
            method: 'put',
            url: `/api/v2/companies/${companyId}/settings/permissions`,
            data: { role }
        });
    }

    updateContactEnrichmentEnabled(companyId: string, value: boolean) {
        return this.apiService.apiRequest<void>({
            method: 'put',
            url: `/api/v2/companies/${companyId}/contact_enrichment_enabled`,
            data: { value }
        });
    }

    registerCompanyLogo(companyId: string, image: IImage) {
        return this.apiService.apiRequest<{ logo_image: IImage }>({
            method: 'post',
            url: `/api/v2/companies/${companyId}/photo/logo`,
            data: image
        });
    }

    /**
     * Not used until react migration of upload image modal is done
     */
    // registerCompanyCover(companyId: string, image: IImage) {
    //     return this.apiService.apiRequest<{ cover_image: IImage }>({
    //         method: 'post',
    //         url: `/api/v2/companies/${companyId}/photo/cover`,
    //         data: image
    //     });
    // }

    /**
     * Not used until react migration of upload image modal is done
     */
    // registerCompanyIcon(companyId: string, image: IImage) {
    //     return this.apiService.apiRequest<{ icon_image: IImage }>({
    //         method: 'post',
    //         url: `/api/v2/companies/${companyId}/photo/icon`,
    //         data: image
    //     });
    // }

    updateCompanyDefaultImage(companyId: string, imageId: string) {
        return this.apiService.apiRequest<void>({
            method: 'put',
            url: `/api/v2/companies/${companyId}/cover_images/default`,
            data: { image_id: imageId }
        });
    }

    updateCompanyImage(imageId: string, newImage: IImage) {
        return this.apiService.apiRequest<IImage[]>({
            method: 'put',
            url: `/api/v2/images/${imageId}`,
            data: newImage
        });
    }

    deleteCompanyIcon(companyId: string) {
        return this.apiService.apiRequest<void>({
            method: 'delete',
            url: `/api/v2/companies/${companyId}/logo/company-icon`
        });
    }

    deleteCompanyLogo(companyId: string) {
        return this.apiService.apiRequest<void>({
            method: 'delete',
            url: `/api/v2/companies/${companyId}/logo/company-logo`
        });
    }

    updateContactLastInteractionAutoUpdateEnabled(
        companyId: string,
        value: boolean
    ) {
        return this.apiService.apiRequest<void>({
            method: 'put',
            url: `/api/v2/companies/${companyId}/contact_last_interaction_auto_update_enabled`,
            data: { value }
        });
    }

    async getUserPendingTeamMemberInvite(userId: string) {
        return this.apiService.apiRequest<IPendingTeamMemberInvite[]>({
            url: `/api/v2/user/${userId}/get_user_pending_invite`,
            method: 'GET'
        });
    }

    async declinePendingInvite(userId: string, inviteId: string) {
        return this.apiService.apiRequest<void>({
            url: `/api/v2/user/${userId}/decline_pending_invite`,
            method: 'PUT',
            data: { invite_id: inviteId }
        });
    }

    sendClientPortalLink(
        companyId: string,
        { toUser, subject, body, workspaceId }: ISendClientPortalLinkData
    ) {
        return this.apiService.apiRequest<void>({
            method: 'post',
            url: `/api/v2/companies/${companyId}/client_portal/email`,
            data: {
                email: toUser.email,
                subject,
                body,
                workspace_id: workspaceId
            }
        });
    }

    async saveAsPayable(
        companyId: string,
        payableId: string,
        payableType: 'invoice' | 'proposal',
        title: string
    ): Promise<any> {
        return this.apiService.apiRequest({
            method: 'POST',
            url: `/api/v2/companies/${companyId}/payable/${payableType}`,
            data: {
                title: title,
                save_as: true,
                payable_id: payableId
            }
        });
    }

    async saveAsTemplate(
        templateType:
            | 'invoice'
            | 'proposal'
            | 'agreement'
            | 'questionnaire'
            | 'brochure'
            | 'timeline',
        companyId: string,
        workspaceFileId: string,
        title: string
    ): Promise<any> {
        return this.apiService.apiRequest({
            method: 'POST',
            url: `/api/v2/companies/${companyId}/templates/${templateType}`,
            data: { title: title, workspace_id: workspaceFileId }
        });
    }

    async createInvoice(
        companyId: string,
        data: any = null
    ): Promise<Record<string, any>> {
        return this.apiService.apiRequest({
            method: 'POST',
            url: `/api/v2/companies/${companyId}/payable/invoice`,
            data
        });
    }

    async getInvoice(companyId: string, invoiceId: string): Promise<any> {
        return this.apiService.apiRequest({
            method: 'GET',
            url: `/api/v2/companies/${companyId}/payable/invoice/${invoiceId}`,
            params: null
        });
    }

    async deleteInvoice(companyId: string, invoiceId: string): Promise<any> {
        return this.apiService.apiRequest({
            method: 'DELETE',
            url: `/api/v2/companies/${companyId}/payable/invoice/${invoiceId}`,
            data: null
        });
    }

    async saveAsInvoice(
        companyId: string,
        template: ITemplateBase,
        title: string
    ): Promise<any> {
        return this.saveAsPayable(companyId, template._id, 'invoice', title);
    }

    async saveAsInvoiceTemplate(
        companyId: string,
        workspaceFileId: string,
        title: string
    ) {
        return this.saveAsTemplate(
            'invoice',
            companyId,
            workspaceFileId,
            title
        );
    }

    async createProposal(companyId: string, data: any = null): Promise<any> {
        return this.apiService.apiRequest({
            method: 'POST',
            url: `/api/v2/companies/${companyId}/payable/proposal`,
            data
        });
    }

    async getProposal(companyId: string, proposalId?: string): Promise<any> {
        return this.apiService.apiRequest({
            method: 'GET',
            url: `/api/v2/companies/${companyId}/payable/proposal/${proposalId}`,
            params: null
        });
    }

    async deleteProposal(companyId: string, proposalId?: string): Promise<any> {
        return this.apiService.apiRequest({
            method: 'DELETE',
            url: `/api/v2/companies/${companyId}/payable/proposal/${proposalId}`,
            data: null
        });
    }

    async saveAsProposal(
        companyId: string,
        template: ITemplateBase,
        title: string
    ): Promise<any> {
        return this.saveAsPayable(companyId, template._id, 'proposal', title);
    }

    async saveAsProposalTemplate(
        companyId: string,
        workspaceFileId: string,
        title: string
    ): Promise<any> {
        return this.saveAsTemplate(
            'proposal',
            companyId,
            workspaceFileId,
            title
        );
    }

    async createPackage(companyId: string, data: any = null): Promise<any> {
        return this.apiService.apiRequest({
            method: 'POST',
            url: `/api/v2/companies/${companyId}/package`,
            data
        });
    }

    async getPackage(companyId: string, packageId?: string): Promise<any> {
        return this.apiService.apiRequest({
            method: 'GET',
            url: `/api/v2/companies/${companyId}/package/${packageId}`,
            params: null
        });
    }

    async deletePackage(companyId: string, packageId: string): Promise<any> {
        return this.apiService.apiRequest({
            method: 'DELETE',
            url: `/api/v2/companies/${companyId}/package/${packageId}`,
            data: null
        });
    }

    async saveAsPackage(
        companyId: string,
        template: ITemplateBase,
        title: string
    ): Promise<any> {
        return this.createPackage(companyId, {
            title: title,
            save_as: true,
            package_to_copy: template._id
        });
    }

    async createAgreement(companyId: string, data: any = null): Promise<any> {
        return this.apiService.apiRequest({
            method: 'POST',
            url: `/api/v2/companies/${companyId}/agreement`,
            data
        });
    }

    async getAgreement(companyId: string, agreementId?: string): Promise<any> {
        return this.apiService.apiRequest({
            method: 'GET',
            url: `/api/v2/companies/${companyId}/agreement/${agreementId}`,
            params: null
        });
    }

    async deleteAgreement(
        companyId: string,
        agreementId?: string
    ): Promise<any> {
        return this.apiService.apiRequest({
            method: 'DELETE',
            url: `/api/v2/companies/${companyId}/agreement/${agreementId}`,
            data: null
        });
    }

    async saveAsAgreement(
        companyId: string,
        template: ITemplateBase,
        title: string
    ): Promise<any> {
        return this.createAgreement(companyId, {
            name: title,
            save_as: true,
            agreement_id: template._id
        });
    }

    async saveAsAgreementTemplate(
        companyId: string,
        workspaceFileId: string,
        title: string
    ): Promise<any> {
        return this.saveAsTemplate(
            'agreement',
            companyId,
            workspaceFileId,
            title
        );
    }

    async setAgreementAsDefault(
        companyId: string,
        agreementId: string
    ): Promise<any> {
        return this.apiService.apiRequest({
            method: 'PUT',
            url: `/api/v2/companies/${companyId}/agreements/${agreementId}/default`,
            data: null
        });
    }

    async createQuestionnaire(
        companyId: string,
        data: any = null
    ): Promise<any> {
        return this.apiService.apiRequest({
            method: 'POST',
            url: `/api/v2/companies/${companyId}/questionnaire`,
            data
        });
    }

    async getQuestionnaire(
        companyId: string,
        questionnaireId?: string
    ): Promise<any> {
        return this.apiService.apiRequest({
            method: 'GET',
            url: `/api/v2/companies/${companyId}/questionnaire/${questionnaireId}`,
            params: null
        });
    }

    async deleteQuestionnaire(
        companyId: string,
        questionnaireId?: string
    ): Promise<any> {
        return this.apiService.apiRequest({
            method: 'DELETE',
            url: `/api/v2/companies/${companyId}/questionnaire/${questionnaireId}`,
            data: null
        });
    }

    async saveAsQuestionnaire(
        companyId: string,
        template: ITemplateBase,
        title: string
    ): Promise<any> {
        return this.createQuestionnaire(companyId, {
            title: title,
            save_as: true,
            questionnaire_id: template._id
        });
    }

    async saveAsQuestionnaireTemplate(
        companyId: string,
        workspaceFileId: string,
        title: string
    ): Promise<any> {
        return this.saveAsTemplate(
            'questionnaire',
            companyId,
            workspaceFileId,
            title
        );
    }

    async createBrochure(companyId: string, data: any = null): Promise<any> {
        return this.apiService.apiRequest({
            method: 'POST',
            url: `/api/v2/companies/${companyId}/templates`,
            data: { ...data, template_type: 'brochure' }
        });
    }

    async getBrochure(
        companyId: string,
        brochureId: string,
        isNewBrochure: boolean
    ): Promise<any> {
        if (isNewBrochure) {
            return this.apiService.apiRequest({
                method: 'GET',
                url: `/api/v2/companies/${companyId}/templates/${brochureId}`
            });
        } else {
            return this.apiService.apiRequest({
                method: 'GET',
                url: `/api/v2/companies/${companyId}/brochure/${brochureId}`
            });
        }
    }

    async deleteBrochure(
        companyId: string,
        brochureId: string,
        isNewBrochure: boolean
    ): Promise<any> {
        if (isNewBrochure) {
            return this.apiService.apiRequest({
                method: 'DELETE',
                url: `/api/v2/companies/${companyId}/templates/${brochureId}`
            });
        } else {
            return this.apiService.apiRequest({
                method: 'DELETE',
                url: `/api/v2/companies/${companyId}/brochure/${brochureId}`
            });
        }
    }

    async saveAsBrochure(
        companyId: string,
        template: ITemplateBase,
        title: string,
        isNewBrochure: boolean
    ): Promise<any> {
        let payload: {
            title: string;
            origin_model_id?: string;
            origin_model_type?: string;
            save_as?: boolean;
            brochure_id?: string;
        } = {
            title
        };

        if (isNewBrochure) {
            payload = {
                ...payload,
                origin_model_id: template._id,
                origin_model_type: 'template'
            };
        } else {
            payload = {
                ...payload,
                save_as: true,
                brochure_id: template._id
            };
        }

        return this.createBrochure(companyId, payload);
    }

    async saveAsBrochureTemplate(
        companyId: string,
        workspaceFileId: string,
        title: string,
        isNewBrochure: boolean
    ): Promise<any> {
        if (isNewBrochure) {
            this.createBrochure(companyId, {
                title: title,
                origin_model_id: workspaceFileId,
                origin_model_type: 'file'
            });
        } else {
            return this.saveAsTemplate(
                'brochure',
                companyId,
                workspaceFileId,
                title
            );
        }
    }

    async saveAsTimelineTemplate(
        companyId: string,
        workspaceFileId: string,
        title: string
    ): Promise<any> {
        return this.saveAsTemplate(
            'timeline',
            companyId,
            workspaceFileId,
            title
        );
    }

    async deleteTimeline(companyId: string, timelineId?: string): Promise<any> {
        return this.apiService.apiRequest({
            method: 'DELETE',
            url: `/api/v2/companies/${companyId}/templates/timeline/${timelineId}`,
            data: null
        });
    }

    async createContactForm(companyId: string, data: any = null): Promise<any> {
        return this.apiService.apiRequest({
            method: 'POST',
            url: `/api/v2/companies/${companyId}/contact_form`,
            data: { contact_form_flow_type: 'default', ...data }
        });
    }

    async getContactForm(
        companyId: string,
        contactFormId: string
    ): Promise<any> {
        return this.apiService.apiRequest({
            method: 'GET',
            url: `/api/v2/companies/${companyId}/contact_form/${contactFormId}`,
            params: null
        });
    }

    async saveAsContactForm(
        companyId: string,
        template: ITemplateBase,
        title: string
    ): Promise<any> {
        return this.createContactForm(companyId, {
            title: title,
            save_as: true,
            contact_form_id: template._id
        });
    }

    async deleteContactForm(
        companyId: string,
        contactFormId?: string
    ): Promise<any> {
        return this.apiService.apiRequest({
            method: 'DELETE',
            url: `/api/v2/companies/${companyId}/contact_form/${contactFormId}`,
            data: null
        });
    }

    async createGiftForm(companyId: string): Promise<any> {
        return this.createContactForm(companyId, {
            contact_form_flow_type: 'gift_card'
        });
    }

    async setAsDefaultCoverImage(companyId: string, image: IImage) {
        await this.apiService.apiRequest({
            method: 'put',
            url: `/api/v2/companies/${companyId}/cover_images/default`,
            data: { image_id: image._id }
        });
    }

    async deleteCoverImage(companyId: string, imageId: string) {
        await this.apiService.apiRequest({
            method: 'delete',
            url: `/api/v2/companies/${companyId}/cover_images/${imageId}`
        });
    }

    async updateImageInDB({
        companyId,
        image,
        uploadType
    }: {
        companyId: string;
        image: IImage;
        uploadType: TImageUploadType;
    }) {
        return this.apiService.apiRequest<IUpdateImageResponse>({
            method: 'post',
            url: `/api/v2/companies/${companyId}/photo/${uploadType}`,
            data: image
        });
    }
}
